import { computed } from 'mobx'
import { truncate } from 'lodash-es'
import { type Observable, Subject } from 'rxjs'
import type { ActivationLifecycle } from '@/infra/activation'
import type { Question } from '@/features/jobs/questions/questions'
import { QuestionValidationResult } from '@/features/jobs/questions/question-validation-result'

/**
 * Base view-model for question answer editors.
 */
export abstract class QuestionAnswerEditorViewModelBase<
  T extends Question,
> implements ActivationLifecycle {
  /**
   * Private subject used to notify of answer changes.
   */
  private readonly _answerChangedSubject = new Subject<void>()

  /**
   * The question's type.
   */
  abstract readonly type: T['type']

  /**
   * Whether the question has been answered. The answer MAY be invalid.
   */
  abstract get isAnswered(): boolean

  /**
   * The current state of the VM as an answer object, `null` when there is
   * nothing to send.
   */
  abstract get answer(): T['answer']

  /**
   * Emits when the answer is changed, but only due to some form of user input.
   */
  get answerChanged$(): Observable<unknown> {
    return this._answerChangedSubject.asObservable()
  }

  /**
   * The answer as a text string.
   */
  @computed
  get textAnswer(): string {
    return this.formatTextAnswer(Infinity)
  }

  /**
   * The answer as a text string of at most `maxLength` characters.
   *
   * @param maxLength
   */
  truncatedTextAnswer(maxLength: number): string {
    return truncate(this.formatTextAnswer(maxLength), {
      length: maxLength,
      separator: ', ',
      omission: '...',
    })
  }

  /** @inheritDoc */
  activate(): unknown {
    // No-op — can be overridden
    return
  }

  /** @inheritDoc */
  deactivate(): unknown {
    // No-op — can be overridden
    return
  }

  /**
   * Commits any pending input. An editor with pending changes updates its
   * answer and calls {@link notifyAnswerChanged}.
   *
   * @virtual
   */
  flush(): unknown {
    // No-op — can be overridden
    return
  }

  /**
   * Sets the editor's properties from the question.
   *
   * @param question
   */
  abstract set(question: T): void

  /**
   * Validates the question answer.
   *
   * @virtual
   */
  validate(): QuestionValidationResult {
    return QuestionValidationResult.ok()
  }

  /**
   * Formats the answer as text. Editors with several values fit as many as
   * possible into `maxLength`.
   *
   * @param maxLength
   */
  protected abstract formatTextAnswer(maxLength: number): string

  /**
   * Notifies that the answer has changed.
   *
   * @protected
   */
  protected notifyAnswerChanged() {
    this._answerChangedSubject.next()
  }
}
